import { useSelector } from 'react-redux';
import {
    ALL_USER_ROLES,
    APPLICATION_ELIGIBILITY_STATUS,
    ApplicationStatus,
    CsrOptionName,
    CsrOptionsProps,
    IApplication,
    useAppSelector,
    useModal,
} from '../utils';
import useApiHandling from './useApiHandling';
import useGetOpenShoppingWindowDetails from './useGetOpenShoppingWindowDetails';
import { useIsSepQepDenialButtonAccessible } from './useIsSepQepDenialButtonAccessible';

export const useCsrOptionsConditions = ({
    application,
    applicationIndex,
    outBoundButtonStatus,
    myappOveedApplnLockJs,
    myAppEndSEPJs,
}: CsrOptionsProps) => {
    const { openModal } = useModal();
    const { userPermissionList, insideOE } = useAppSelector(state => state.capApplications.capApplicationsInfo);
    const hasPermission = (permission: string): boolean => userPermissionList.includes(permission);
    const isState = useSelector((state: any) => state?.common?.environment?.isState);
    const { handleGetOverrideEligibilityStartDate, fetchProgrmeEligibility, fetchSepEventDetails } = useApiHandling();
    const { fetchOpenShoppingWindowDetails } = useGetOpenShoppingWindowDetails();
    const userActiveRoleName = useAppSelector(
        state => state?.common?.environment?.user?.activeModuleName?.toUpperCase() as string,
    );

    const showEndSep = (application: IApplication): boolean => {
        const isEligibleStatus = [
            ApplicationStatus.EligibilityReceived,
            ApplicationStatus.PartiallyEnrolled,
            ApplicationStatus.EnrolledOrActive,
        ].includes(application.applicationStatus as ApplicationStatus);

        const isEligibleRole = [ALL_USER_ROLES.ADMIN, ALL_USER_ROLES.SUPERVISOR].includes(userActiveRoleName);

        const isSpecialCase = !isState.ID && userActiveRoleName === ALL_USER_ROLES.L2_CSR;

        return !insideOE && isEligibleStatus && application.endSepButtonEnabled && (isEligibleRole || isSpecialCase);
    };

    const openCSR = (caseNumber: string, typeOfCsrAction: string, coverageYear: number) => {
        openModal('csrOverrideCommentModal', {
            caseNumber: caseNumber,
            typeOfCsrAction: typeOfCsrAction,
            coverageYear: coverageYear,
            applicationType: application?.appType,
        });
    };

    const openOverrideSepCsr = (caseNumber: string, typeOfCsrAction: string, coverageYear: number) => {
        openModal('csrOverrideCommentModal', {
            caseNumber: caseNumber,
            typeOfCsrAction: typeOfCsrAction,
            coverageYear: coverageYear,
            applicationType: application?.appType,
        });
    };

    const getOpenShoppingWindowDetails = (application: IApplication) => {
        fetchOpenShoppingWindowDetails(application);
    };

    const openEndSepModal = (application: IApplication, typeOfCsrAction: string, coverageYear: number) => {
        openModal('csrOverrideCommentModal', {
            caseNumber: application.caseNumber,
            typeOfCsrAction: typeOfCsrAction,
            coverageYear: coverageYear,
            applicationType: application?.appType,
        });
    };

    const getOverrideApplicationEditLock = (application: IApplication) => {
        openModal('csrOverrideCommentModal', {
            caseNumber: application.caseNumber,
            typeOfCsrAction: CsrOptionName.OVERRIDE_APP_EDIT_LOCK,
            applicationType: application?.appType,
        });
    };

    const getOverrideEligibilityStartDate = (application: IApplication) => {
        handleGetOverrideEligibilityStartDate(application.caseNumber, application.coverageYear);
    };

    return [
        {
            check: hasPermission('IND_PORTAL_EDIT_SPCL_ENRLMNT_PERIOD') && application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.SPECIAL_ENROLLMENT, application.coverageYear),
            messageCode: 'cap.applications.csroption.openspecialenorollment',
        },
        {
            check:
                hasPermission('MYAPP_CHANGE_COVRAGE_STARTDATE') &&
                application.isApplicationCurrent &&
                applicationIndex === 0,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.COVERAGE_DATE, application.coverageYear),
            messageCode: 'cap.applications.csroption.changecoveragedate',
        },
        {
            check:
                hasPermission('MYAPP_EDIT_NONFIN_APP') &&
                [
                    ApplicationStatus.EligibilityReceived,
                    ApplicationStatus.PartiallyEnrolled,
                    ApplicationStatus.EnrolledOrActive,
                    ApplicationStatus.Signed,
                ].includes(application.applicationStatus as ApplicationStatus) &&
                (!isState.ID || application.isNonFinancial) &&
                application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.EDIT, application.coverageYear),
            messageCode: 'cap.applications.csroption.editapplication',
        },
        {
            check:
                hasPermission('MYAPP_INIT_NONFIN_VERIFICATION') &&
                application.isNonFinancial &&
                application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.INITIATE, application.coverageYear),
            messageCode: 'cap.applications.csroption.initiateverifications',
        },
        {
            check:
                hasPermission('MYAPP_RTN_NONFIN_ELIGIBILITY') &&
                application.isNonFinancial &&
                application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.RE_RUN, application.coverageYear),
            messageCode: 'cap.applications.csroption.reruneligibility',
        },
        {
            check:
                hasPermission('MYAPP_CANCEL_TERMPLAN') &&
                [ApplicationStatus.PartiallyEnrolled, ApplicationStatus.EnrolledOrActive].includes(
                    application.applicationStatus as ApplicationStatus,
                ) &&
                application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.CANCEL_TERM, application.coverageYear),
            messageCode: 'cap.applications.csroption.cancelorterminatemyplan',
        },
        {
            check:
                hasPermission('MYAPP_OPEN_PLAN_SHOPPING_WINDOW') &&
                applicationIndex === 0 &&
                application.isExchangeEligible &&
                application.isLatestApplication,
            onClick: () => getOpenShoppingWindowDetails(application),
            messageCode: 'cap.applications.csroption.openshoppingwindow',
        },
        {
            check:
                hasPermission('MYAPP_SEND_DEMOGRPHCUPDT_CARRIER') &&
                application.isNonFinancial &&
                application.applicationStatus !== ApplicationStatus.EligibilityReceived &&
                application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.UPDATE, application.coverageYear),
            messageCode: 'cap.applications.csroption.updatecarrier',
        },
        {
            check:
                hasPermission('MYAPP_OVERRD_SPCL_ENROLLMENT') &&
                application.applicationStatus === ApplicationStatus.EligibilityReceived &&
                (application.isQep || application.isSepApp) &&
                !(application.eligibilityStatus === APPLICATION_ELIGIBILITY_STATUS && application.isNonFinancial) &&
                application.isApplicationCurrent,
            onClick: () => fetchSepEventDetails(application),
            messageCode: 'cap.applications.csroption.overrideSpecialEnrollment',
        },
        {
            check:
                hasPermission('MYAPP_OVERRD_APTCELGBLT_STARTDT') &&
                application.isSepApp &&
                application.applicationStatus === ApplicationStatus.EligibilityReceived &&
                !application.isNonFinancial &&
                application.isApplicationCurrent,
            onClick: () => getOverrideEligibilityStartDate(application),
            messageCode: 'cap.applications.csroption.overrideAPTCEligibilityStartDate',
        },
        {
            check:
                hasPermission('MYAPP_SEND_OUTBOUND_AT') &&
                applicationIndex === 0 &&
                outBoundButtonStatus &&
                application.isApplicationCurrent,
            onClick: () => openCSR(application.caseNumber, CsrOptionName.SEND_OUTBOUND_AT, application.coverageYear),
            messageCode: 'cap.applications.csroption.sendOutboundAT',
        },
        {
            check:
                hasPermission('MYAPP_OVERRIDE_PROG_ELIG') &&
                [
                    ApplicationStatus.EligibilityReceived,
                    ApplicationStatus.PartiallyEnrolled,
                    ApplicationStatus.EnrolledOrActive,
                ].includes(application.applicationStatus as ApplicationStatus) &&
                application.isApplicationCurrent,
            onClick: () => fetchProgrmeEligibility(application),
            messageCode: 'cap.applications.csroption.overrideProgramEligibility',
        },
        {
            check:
                myappOveedApplnLockJs.toLowerCase() === 'true' &&
                hasPermission('MYAPP_OVERRD_APPLN_LOCK') &&
                application.magiReferralLock &&
                application.isApplicationCurrent,
            onClick: () => getOverrideApplicationEditLock(application),
            messageCode: 'cap.applications.csroption.overrideApplicationEditLock',
        },
        {
            check:
                myAppEndSEPJs.toLowerCase() === 'true' &&
                hasPermission('MYAPP_OVERRD_APPLN_END_SEP') &&
                showEndSep(application) &&
                application.isApplicationCurrent,
            onClick: () => openEndSepModal(application, CsrOptionName.END_SEP, application.coverageYear),
            messageCode: 'cap.applications.csroption.endSep',
        },
        {
            check:
                hasPermission('IND_PORTAL_OVERRIDE_SEP_DENIAL') &&
                useIsSepQepDenialButtonAccessible(application) &&
                !application.isApplicationCurrent,
            onClick: () =>
                openOverrideSepCsr(application.caseNumber, CsrOptionName.OVERRIDE_SEP_DENIAL, application.coverageYear),
            messageCode: 'cap.applications.csroption.overridesepdenialbtnheading',
        },
    ];
};
